import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ShieldCheck, ShieldAlert, AlertOctagon, Activity,
  RefreshCw, ScanLine, CheckCircle2, XCircle, Eye, Sparkles, Clock, User, Send, Zap,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_META = {
  clinical_anomaly: { label: 'Clinical anomaly', icon: AlertOctagon },
  missed_vitals: { label: 'Missed vitals', icon: Clock },
  vitals_anomaly: { label: 'Vitals anomaly', icon: Activity },
};

const SEV_STYLE = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  low: 'bg-slate-100 text-slate-600 dark:bg-cn-ink-700 dark:text-slate-300',
};
const SEV_DOT = { critical: 'bg-red-600', high: 'bg-orange-500', medium: 'bg-amber-500', low: 'bg-slate-400' };
const STATUS_STYLE = {
  open: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  reviewing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  dismissed: 'bg-slate-100 text-slate-500 dark:bg-cn-ink-700 dark:text-slate-400',
};

const FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'all', label: 'All' },
];

const StatTile = ({ testid, label, value, icon: Icon, tone }) => (
  <div
    data-testid={testid}
    className="p-4 rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-2xl font-bold text-slate-900 dark:text-white font-display mt-1">{value}</p>
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

export const SafetyShieldPanel = () => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [auditing, setAuditing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [escalatingId, setEscalatingId] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [note, setNote] = useState('');

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filter === 'all' ? '' : `?status=${filter}`;
      const [sumRes, evRes] = await Promise.all([
        axios.get(`${API}/safety/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/safety/events${statusParam}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSummary(sumRes.data);
      setEvents(evRes.data.events || []);
    } catch (e) {
      toast.error('Failed to load Safety Shield queue');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await axios.post(`${API}/safety/audit/run`, {}, authHeader);
      toast.success(`Audit complete — ${data.events_created} new event(s) across ${data.patients_scanned} patients`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed to run');
    } finally {
      setAuditing(false);
    }
  };

  const updateEvent = async (ev, status) => {
    setBusyId(ev.id);
    try {
      await axios.patch(
        `${API}/safety/events/${ev.id}`,
        { status, resolution_note: note || null },
        authHeader,
      );
      const verb = { reviewing: 'moved to review', resolved: 'resolved', dismissed: 'dismissed' }[status];
      toast.success(`${ev.patient_initials} — event ${verb}`);
      setDrawerId(null);
      setNote('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update event');
    } finally {
      setBusyId(null);
    }
  };

  const openDrawer = (id) => {
    setNote('');
    setDrawerId((cur) => (cur === id ? null : id));
  };

  const escalate = async (ev) => {
    setEscalatingId(ev.id);
    try {
      const { data } = await axios.post(`${API}/safety/events/${ev.id}/escalate`, {}, authHeader);
      const mins = data.time_to_escalate_minutes ?? 0;
      toast.success(`Escalated to ${ev.clinician_name || 'clinician'} — acted in ${mins} min (in-app + SMS)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not escalate event');
    } finally {
      setEscalatingId(null);
    }
  };

  const s = summary || {};

  return (
    <div data-testid="safety-shield-panel">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="safety-summary">
        <StatTile testid="safety-stat-critical" label="Open critical" value={s.open_critical ?? 0} icon={AlertOctagon} tone="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300" />
        <StatTile testid="safety-stat-high" label="Open high" value={s.open_high ?? 0} icon={ShieldAlert} tone="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300" />
        <StatTile testid="safety-stat-open" label="In queue (open + reviewing)" value={(s.open ?? 0) + (s.reviewing ?? 0)} icon={Activity} tone="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300" />
        <StatTile testid="safety-stat-resolved" label="Resolved (audit trail)" value={s.resolved ?? 0} icon={ShieldCheck} tone="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300" />
      </div>

      {/* Filters + actions */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`safety-filter-${f.value}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-cn-primary text-white' : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={runAudit}
            disabled={auditing}
            data-testid="safety-run-audit-btn"
            className="cn-btn cn-btn-primary !py-2 !px-3 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {auditing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
            {auditing ? 'Running AI audit…' : 'Run AI audit'}
          </button>
          <button
            onClick={load}
            data-testid="safety-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300 hover:text-cn-primary transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>
      ) : events.length === 0 ? (
        <div className="py-12 text-center" data-testid="safety-empty">
          <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Queue clear</h3>
          <p className="text-slate-500 dark:text-slate-400 mt-1">No safety events in this view. Run the AI audit to scan active patients.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> {events.length} event(s) — most urgent first
          </p>
          {events.map((ev) => {
            const meta = EVENT_META[ev.event_type] || { label: ev.event_type, icon: ShieldAlert };
            const Icon = meta.icon;
            const busy = busyId === ev.id;
            const open = drawerId === ev.id;
            const actionable = ev.status === 'open' || ev.status === 'reviewing';
            return (
              <div
                key={ev.id}
                data-testid={`safety-event-${ev.id}`}
                className="rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800"
              >
                <div className="p-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${SEV_DOT[ev.severity] || 'bg-slate-400'}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon className="w-4 h-4 text-slate-400" />
                        <p className="font-semibold text-slate-900 dark:text-white">{ev.summary}</p>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLE[ev.severity] || ''}`}>{ev.severity}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_STYLE[ev.status] || ''}`}>{ev.status}</span>
                        {ev.escalated && (
                          <span data-testid={`safety-escalated-badge-${ev.id}`}
                            className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 inline-flex items-center gap-1">
                            <Zap className="w-3 h-3" /> Escalated · {ev.time_to_escalate_minutes ?? 0}m
                          </span>
                        )}
                        {ev.ai_generated && (
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 inline-flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> AI
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                        <span className="inline-flex items-center gap-1 font-medium text-slate-700 dark:text-slate-200">
                          <User className="w-3 h-3" /> Patient {ev.patient_initials}
                        </span>
                        <span className="inline-flex items-center gap-1">{meta.label}</span>
                        {ev.clinician_name && ev.clinician_name !== '—' && (
                          <span className="inline-flex items-center gap-1">Clinician: {ev.clinician_name}</span>
                        )}
                      </div>
                      <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">{ev.detail}</p>
                      {ev.resolution_note && (
                        <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-300" data-testid={`safety-resolution-${ev.id}`}>
                          Resolution: {ev.resolution_note}
                        </p>
                      )}
                    </div>
                  </div>
                  {actionable && (
                    <div className="shrink-0 flex items-center gap-2 flex-wrap">
                      {ev.clinician_id && ev.clinician_name !== '—' && !ev.escalated && (
                        <button onClick={() => escalate(ev)} disabled={escalatingId === ev.id} data-testid={`safety-escalate-${ev.id}`}
                          title="Notify the patient's clinician now (in-app + SMS)"
                          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-100 inline-flex items-center gap-1.5 disabled:opacity-50">
                          {escalatingId === ev.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Escalate to clinician
                        </button>
                      )}
                      {ev.status === 'open' && (
                        <button onClick={() => updateEvent(ev, 'reviewing')} disabled={busy} data-testid={`safety-review-${ev.id}`}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 inline-flex items-center gap-1.5 disabled:opacity-50">
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Start review
                        </button>
                      )}
                      <button onClick={() => openDrawer(ev.id)} data-testid={`safety-action-${ev.id}`}
                        className="cn-btn cn-btn-primary !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve / Dismiss
                      </button>
                    </div>
                  )}
                </div>

                {open && actionable && (
                  <div className="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-cn-ink-700" data-testid={`safety-drawer-${ev.id}`}>
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Resolution note (logged to the immutable audit trail)</label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      data-testid={`safety-resolution-note-${ev.id}`}
                      rows={2}
                      placeholder="e.g. Called clinician; patient escalated to PCP; vitals re-logged."
                      className="mt-1.5 w-full text-sm rounded-lg border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-900 p-2.5 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-cn-primary/40"
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={() => updateEvent(ev, 'resolved')} disabled={busy} data-testid={`safety-resolve-${ev.id}`}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5 disabled:opacity-50">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Mark resolved
                      </button>
                      <button onClick={() => updateEvent(ev, 'dismissed')} disabled={busy} data-testid={`safety-dismiss-${ev.id}`}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-300 hover:text-red-600 inline-flex items-center gap-1.5 disabled:opacity-50">
                        <XCircle className="w-3.5 h-3.5" /> Dismiss
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SafetyShieldPanel;
